import { Container, Graphics } from "../../../vendor/pixi.min.mjs";
import { CollisionSystem } from "../../systems/CollisionSystem.js";
import { FROGGER_CONFIG } from "./froggerConfig.js";

const {
  CELL_SIZE,
  COLS,
  BIRD_SPAWN_INTERVAL,
  BIRD_SPEED,
  BIRD_SIZE,
  BIRD_COLOR,
} = FROGGER_CONFIG;

/**
 * Curved-path tuning. The curve is a single sin-wave perpendicular to the
 * bird's velocity, so each bird traces a smooth arc instead of a straight
 * line. `BIRD_CURVE_RATE` is radians of phase added per frame — small values
 * keep the arc gentle. Default tuning produces roughly half a sine wave
 * across a typical bird's flight (one nice swoop).
 */
const BIRD_CURVE_PROB = 0.55;
const BIRD_CURVE_AMP_PX = [60, 130];
const BIRD_CURVE_RATE = [0.005, 0.014];

/**
 * Wing-flap tuning. The bird's inner sprite is squashed vertically by a sine
 * wave to simulate wings beating up and down. `BIRD_FLAP_RATE` is radians of
 * phase added per frame at 60fps → roughly 2.7–4.3 flaps/sec, which reads as
 * a small bird's wingbeat. `BIRD_FLAP_AMP` is the peak vertical compression
 * (1.0 - amp = thinnest profile during the down-beat).
 */
const BIRD_FLAP_RATE = [0.28, 0.45];
const BIRD_FLAP_AMP = 0.18;

/**
 * Feather-trail tuning. While trails are enabled, every bird sheds tiny
 * feathers every `BIRD_TRAIL_INTERVAL` frames from a disc centred on the
 * bird's body. Feathers spawn inside the disc (radius =
 * BIRD_TRAIL_SPAWN_RADIUS × BIRD_SIZE) and fly outward, so they look
 * like they're emerging from the bird rather than dripping from a tail.
 * Particles inside the bird's silhouette are briefly hidden by the
 * sprite, then become visible as they fan out — selling the "burst from
 * the body" effect. The colour of each feather is drawn from a palette
 * keyed off the bird's variant (red / blue / purple / pink / yellow).
 */
const BIRD_TRAIL_INTERVAL = 4;
const BIRD_TRAIL_SPAWN_RADIUS = 0.35; // multiplier on BIRD_SIZE

/**
 * Per-variant feather palettes. Keys must match a substring of the bird
 * sprite filename (e.g. `bird-purple.png` → `purple`). Each palette
 * blends the variant's hero hue with a couple of light tints + white so
 * individual feathers read against any sky colour.
 */
const BIRD_TRAIL_PALETTES = {
  red: [0xef4444, 0xdc2626, 0xfca5a5, 0xfee2e2, 0xffffff],
  blue: [0x3b82f6, 0x60a5fa, 0x93c5fd, 0xdbeafe, 0xffffff],
  purple: [0xa855f7, 0x8b5cf6, 0xc084fc, 0xe9d5ff, 0xffffff],
  pink: [0xec4899, 0xf472b6, 0xf9a8d4, 0xfce7f3, 0xffffff],
  yellow: [0xeab308, 0xfbbf24, 0xfde047, 0xfde68a, 0xffffff],
};
const BIRD_TRAIL_DEFAULT_PALETTE = BIRD_TRAIL_PALETTES.red;

/** Pull the colour tag out of a default bird filename (e.g. `bird-blue.png`). */
function _colorTagFromPath(path) {
  if (!path) return null;
  const lower = String(path).toLowerCase();
  for (const tag of Object.keys(BIRD_TRAIL_PALETTES)) {
    if (lower.includes(tag)) return tag;
  }
  return null;
}

/**
 * Manages dive-bombing bird enemies for the Frogger game.
 * Birds spawn above the visible area and dive toward the player's
 * position at spawn time. They attack on ALL rows, including safe zones.
 */
export class FroggerBirds {
  container = new Container();

  /** @type {{ display: Container, x: number, y: number, vx: number, vy: number, active: boolean }[]} */
  _birds = [];

  _originX = 0;
  _screenWidth = 0;
  _spawnTimer = 0;
  _nextSpawn = 0;
  _themeManager = null;

  /** @type {{ burst: (x: number, y: number, kind: string) => void }|null} */
  _burstEmitter = null;
  _trailsEnabled = false;

  constructor(originX, screenWidth, themeManager = null) {
    this._originX = originX;
    this._screenWidth = screenWidth;
    this._themeManager = themeManager;
    this._nextSpawn = this._randInt(
      BIRD_SPAWN_INTERVAL[0],
      BIRD_SPAWN_INTERVAL[1],
    );
  }

  /**
   * Hand the bird system a confetti emitter (e.g. `FroggerPickupBurst`).
   * Trails are still gated on {@link setTrailsEnabled}, so you can wire
   * the emitter once at scene init and toggle the effect at runtime.
   */
  setBurstEmitter(emitter) {
    this._burstEmitter = emitter || null;
  }

  /** Toggle the confetti trail on/off without recreating birds. */
  setTrailsEnabled(enabled) {
    this._trailsEnabled = !!enabled;
  }

  update(delta, playerWorldRow) {
    this._spawnTimer += delta;

    if (this._spawnTimer >= this._nextSpawn) {
      this._spawnTimer = 0;
      this._nextSpawn = this._randInt(
        BIRD_SPAWN_INTERVAL[0],
        BIRD_SPAWN_INTERVAL[1],
      );
      this._spawnBird(playerWorldRow);
    }

    for (let i = this._birds.length - 1; i >= 0; i--) {
      const b = this._birds[i];
      b.baseX += b.vx * delta;
      b.baseY += b.vy * delta;

      if (b.curveAmp > 0) {
        b.curvePhase += b.curveSpeed * delta;
        const offset = Math.sin(b.curvePhase) * b.curveAmp;
        b.x = b.baseX + b.perpX * offset;
        b.y = b.baseY + b.perpY * offset;
      } else {
        b.x = b.baseX;
        b.y = b.baseY;
      }

      b.display.x = b.x;
      b.display.y = b.y;

      if (b.flapTarget) {
        b.flapPhase += b.flapSpeed * delta;
        const squash = 0.5 * (1 - Math.cos(b.flapPhase));
        b.flapTarget.scale.y = 1 - BIRD_FLAP_AMP * squash;
        b.flapTarget.scale.x = 1 + 0.05 * squash;
      }

      if (this._trailsEnabled && this._burstEmitter) {
        b.trailTimer += delta;
        if (b.trailTimer >= BIRD_TRAIL_INTERVAL) {
          b.trailTimer = 0;
          // Spawn somewhere inside a disc centred on the bird body so
          // feathers look like they emerge FROM the bird, not from a
          // tiny point behind it. Polar sampling (sqrt(r) for uniform
          // disc distribution) keeps the cloud shaped like a circle
          // instead of clumping toward the centre.
          const a = Math.random() * Math.PI * 2;
          const r = Math.sqrt(Math.random()) * BIRD_SIZE * BIRD_TRAIL_SPAWN_RADIUS;
          const px = b.x + Math.cos(a) * r;
          const py = b.y + Math.sin(a) * r;
          this._burstEmitter.burst(px, py, "birdTrail", b.trailPalette);
        }
      }

      const birdWorldRow = Math.round(-b.y / CELL_SIZE);
      if (birdWorldRow < playerWorldRow - 10) {
        this.container.removeChild(b.display);
        b.display.destroy();
        this._birds.splice(i, 1);
      }
    }
  }

  checkCollision(playerRect) {
    return !!this.checkCollisionDebug(playerRect);
  }

  checkCollisionDebug(playerRect) {
    const hitW = BIRD_SIZE * 0.6;
    const hitH = BIRD_SIZE * 0.4;
    for (let i = this._birds.length - 1; i >= 0; i--) {
      const b = this._birds[i];
      const br = {
        x: b.x - hitW / 2,
        y: b.y - hitH / 2,
        w: hitW,
        h: hitH,
      };
      if (CollisionSystem.checkRects(playerRect, br)) {
        const info = { x: b.x, y: b.y, vx: b.vx, vy: b.vy };
        this.container.removeChild(b.display);
        b.display.destroy();
        this._birds.splice(i, 1);
        return info;
      }
    }
    return null;
  }

  destroy() {
    for (const b of this._birds) {
      b.display.destroy();
    }
    this._birds.length = 0;
    this.container.removeChildren();
  }

  _spawnBird(playerWorldRow) {
    const gridW = COLS * CELL_SIZE;
    const targetCol = Math.floor(Math.random() * COLS);
    const targetX = this._originX + targetCol * CELL_SIZE + CELL_SIZE / 2;
    const targetY = -playerWorldRow * CELL_SIZE + CELL_SIZE / 2;

    const fromLeft = Math.random() < 0.5;
    const startX = fromLeft
      ? this._originX - BIRD_SIZE * 2
      : this._originX + gridW + BIRD_SIZE * 2;
    const startY = targetY - CELL_SIZE * 8;

    const dx = targetX - startX;
    const dy = targetY - startY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const vx = (dx / dist) * BIRD_SPEED;
    const vy = (dy / dist) * BIRD_SPEED;

    const { display, colorTag } = this._buildBirdDisplay(vx > 0);
    display.x = startX;
    display.y = startY;
    this.container.addChild(display);

    const trailPalette =
      BIRD_TRAIL_PALETTES[colorTag] || BIRD_TRAIL_DEFAULT_PALETTE;

    const wavy = Math.random() < BIRD_CURVE_PROB;
    const curveAmp = wavy
      ? this._rand(BIRD_CURVE_AMP_PX[0], BIRD_CURVE_AMP_PX[1])
      : 0;
    const curveSpeed = wavy
      ? this._rand(BIRD_CURVE_RATE[0], BIRD_CURVE_RATE[1])
      : 0;
    const speed = BIRD_SPEED;
    const perpX = -vy / speed;
    const perpY = vx / speed;
    // Start somewhere in the first quarter so the arc reads as a single
    // graceful swoop rather than a sharp wobble out of the gate.
    const curvePhase = Math.random() * Math.PI * 0.5;

    this._birds.push({
      display,
      x: startX,
      y: startY,
      baseX: startX,
      baseY: startY,
      vx,
      vy,
      perpX,
      perpY,
      curveAmp,
      curveSpeed,
      curvePhase,
      flapTarget: display.flapTarget ?? null,
      flapPhase: Math.random() * Math.PI * 2,
      flapSpeed: this._rand(BIRD_FLAP_RATE[0], BIRD_FLAP_RATE[1]),
      trailTimer: Math.random() * BIRD_TRAIL_INTERVAL,
      trailPalette,
      active: true,
    });
  }

  /**
   * Build the bird display. Returns the container + a colour tag derived
   * from whichever variant ended up on the sprite (e.g. 'purple' for
   * `bird-purple.png`), so the caller can pick a matching trail palette.
   * @returns {{ display: import('../../../vendor/pixi.min.mjs').Container, colorTag: string|null }}
   */
  _buildBirdDisplay(facingRight) {
    let colorTag = null;
    let forcedSrc = null;
    const variants = this._themeManager?.getVariations?.("frogger.bird") || [];
    if (variants.length > 0) {
      forcedSrc = variants[Math.floor(Math.random() * variants.length)];
      colorTag = _colorTagFromPath(forcedSrc);
    }

    const sprite = this._themeManager?.createSprite(
      "frogger.bird",
      BIRD_SIZE,
      BIRD_SIZE,
      {
        anchorX: 0.5,
        anchorY: 0.5,
        flipX: !facingRight,
        src: forcedSrc || undefined,
      },
    );
    if (sprite) {
      const wrap = new Container();
      wrap.addChild(sprite);
      wrap.flapTarget = wrap;
      return { display: wrap, colorTag };
    }

    const g = new Graphics();
    g.flapTarget = g;
    const s = BIRD_SIZE;
    const dir = facingRight ? 1 : -1;

    g.ellipse(0, 0, s * 0.35, s * 0.18);
    g.fill(BIRD_COLOR);

    g.moveTo(-s * 0.15 * dir, -s * 0.05);
    g.lineTo(-s * 0.4 * dir, -s * 0.35);
    g.lineTo(0, -s * 0.1);
    g.closePath();
    g.fill(BIRD_COLOR);

    g.moveTo(s * 0.1 * dir, -s * 0.05);
    g.lineTo(s * 0.35 * dir, -s * 0.3);
    g.lineTo(s * 0.2 * dir, -s * 0.05);
    g.closePath();
    g.fill(BIRD_COLOR);

    g.moveTo(s * 0.35 * dir, 0);
    g.lineTo(s * 0.48 * dir, -s * 0.05);
    g.lineTo(s * 0.48 * dir, s * 0.05);
    g.closePath();
    g.fill(0xf97316);

    g.circle(s * 0.18 * dir, -s * 0.06, s * 0.05);
    g.fill(0xffffff);
    g.circle(s * 0.18 * dir, -s * 0.06, s * 0.025);
    g.fill(0x000000);

    // Procedural bird uses the placeholder BIRD_COLOR (red), so its
    // trail naturally inherits the red feather palette below.
    return { display: g, colorTag: "red" };
  }

  _randInt(min, max) {
    return min + Math.floor(Math.random() * (max - min + 1));
  }

  _rand(min, max) {
    return min + Math.random() * (max - min);
  }
}
